import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import InteractivePlaneIcon from './InteractivePlaneIcon';

export default function ModelInfoModal({ isOpen, onClose, model = null, isPremium, onPremiumFeatureClick }) {
  const [modelData, setModelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchModelData();
      // Set search term when modal opens with a model prop
      if (model) {
        setSearchTerm(model);
      }
    }
  }, [isOpen, model]);

  const fetchModelData = async () => {
    try {
      setLoading(true);
      setError(null);
      const sheetId = process.env.REACT_APP_MODEL_INFO_SHEET_ID;
      const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      
      const response = await fetch(url);
      const csvText = await response.text();
      
      Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const processedData = results.data
            .filter(row => row['Model'])
            .map(row => ({
              model: row['Model'] || '',
              engineType: row['Engine Type'] || '',
              numberOfSeats: row['Number of Seats'] || '',
              length: row['Length'] || '',
              wingspan: row['Wingspan'] || '',
              cruisingSpeed: row['Cruising Speed'] || '',
              wingtips: row['Wingtips'] || '',
              serratedNacelles: row['Serrated Nacelles'] || '',
              remarks: row['Remarks'] || ''
            }));
          
          setModelData(processedData);
          setLoading(false);
        },
        error: (err) => {
          console.error('Model CSV parsing error:', err);
          setError('Failed to parse model data');
          setLoading(false);
        }
      });
    } catch (err) {
      console.error('Failed to fetch model data:', err);
      setError('Failed to fetch model data');
      setLoading(false);
    }
  };

  const filteredData = modelData.filter(m => 
    m.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.engineType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getModelColor = (modelName) => {
    if (modelName.includes('A380')) return 'from-purple-500 to-pink-500';
    if (modelName.includes('A350')) return 'from-blue-500 to-cyan-500';
    if (modelName.includes('787')) return 'from-green-500 to-teal-500';
    if (modelName.includes('777-9')) return 'from-orange-500 to-red-500';
    if (modelName.includes('777')) return 'from-yellow-500 to-orange-500';
    if (modelName.includes('737')) return 'from-indigo-500 to-purple-500';
    return 'from-gray-500 to-gray-600';
  };

  const isModelUnlocked = (modelName) => {
    // A350-900 is always unlocked for everyone
    if (modelName.includes('A350-900')) return true;
    // All other models require premium
    return isPremium;
  };

  const handleCardClick = (aircraft) => {
    if (!isModelUnlocked(aircraft.model)) {
      onPremiumFeatureClick();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[2000] transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-8 bg-white rounded-2xl shadow-2xl z-[2001] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                Aircraft Models
              </h2>
              <p className="text-sm text-gray-600">
                Singapore Airlines Fleet Models
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 md:px-6 md:pb-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 w-full sm:max-w-md">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by model or engine type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="font-semibold text-gray-700">{filteredData.length}</span>
                <span className="text-gray-500"> {filteredData.length === 1 ? 'model' : 'models'}</span>
              </div>
            </div>
          </div>

          {/* Premium Notice */}
          {/* {!isPremium && (
            <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Upgrade to Premium</span> to view all aircraft models
                </p>
              </div>
            </div>
          )} */}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading aircraft models...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 font-semibold mb-2">{error}</p>
                <button
                  onClick={fetchModelData}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-gray-500">No aircraft models found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredData.map((aircraft, index) => {
                const isUnlocked = isModelUnlocked(aircraft.model);
                
                return (
                  <div 
                    key={index} 
                    className={`bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${
                      !isUnlocked ? 'cursor-pointer relative' : ''
                    }`}
                    onClick={() => handleCardClick(aircraft)}
                  >
                    {/* Card Header */}
                    <div className={`bg-gradient-to-r ${getModelColor(aircraft.model)} p-4 text-white ${!isUnlocked ? 'blur-sm select-none' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-xl font-bold">{aircraft.model}</h3>
                      </div>
                      <p className="text-sm opacity-90">{aircraft.remarks}</p>
                    </div>

                    {/* Interactive Plane Icon */}
                    <div className={`p-6 bg-gradient-to-br from-slate-50 to-blue-50 ${!isUnlocked ? 'blur-sm select-none' : ''}`}>
                      <InteractivePlaneIcon aircraft={aircraft} />
                    </div>

                    {/* Lock Overlay for non-premium users */}
                    {!isUnlocked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-40">
                        <div className="text-center">
                          <svg className="w-12 h-12 text-gray-600 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Premium Only</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Disclaimer Footer */}
        <div className="px-4 md:px-6 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Aircraft specifications compiled from public sources and manufacturer data. Information is for reference only.
          </p>
        </div>
      </div>
    </>
  );
}